import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { adminFetch } from '../../lib/adminFetch';

/* =========================================================================
 * 派遣賃金データ（局長通達：一般労働者の賃金水準）
 *   厚労省の公式Excel（別添2 全国平均 / 別添3 地域指数）をアップロードし、
 *   ブラウザ内でSheetJS解析 → 年度ごとにサーバー保存。毎年自己更新できる。
 * ========================================================================= */

const COLS = ['base_0y', 'y1', 'y2', 'y3', 'y5', 'y10', 'y20', 'max_0y', 'ref_0y'];
// 表示用ラベル（暫定）。正式名称の指定があれば差し替え。
const COL_LABELS = {
  base_0y: '基準値(0年)', y1: '1年', y2: '2年', y3: '3年', y5: '5年',
  y10: '10年', y20: '20年', max_0y: '最大値', ref_0y: '参考値',
};
const fmtYen = (v) => (typeof v === 'number' && !isNaN(v)) ? v.toLocaleString('ja-JP') : '—';
const isNum = (v) => typeof v === 'number' && !isNaN(v);
const isDash = (v) => typeof v === 'string' && ['-', '−', 'ー', '—', '‐'].includes(v.trim());

// 別添2 全国平均：名称=最初の非空文字列、値=その後の数値/ダッシュを9個（列位置のズレに強い動的解析）
function parseNational(ws) {
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
  const items = [];
  for (const cells of rows) {
    if (!cells) continue;
    let label = null, start = 0;
    for (let i = 0; i < cells.length; i++) {
      const v = cells[i];
      if (typeof v === 'string' && v.trim() !== '' && !isDash(v)) { label = v.trim(); start = i + 1; break; }
    }
    if (!label) continue;
    if (/^(基準値|職業安定業務統計|別添|（円）|\(円\))/.test(label)) continue; // ヘッダー行除外
    const vals = [];
    for (let i = start; i < cells.length && vals.length < 9; i++) {
      const v = cells[i];
      if (isNum(v)) vals.push(Math.round(v));
      else if (isDash(v)) vals.push(null);
      else if (v === null || v === undefined || v === '') continue;
      else break;
    }
    if (vals.length === 0) continue;
    while (vals.length < 9) vals.push(null);
    const m = label.match(/^(\d+)\s*(.*)$/);
    const rec = { code: m ? m[1] : null, name: (m ? m[2] : label).trim() };
    COLS.forEach((k, i) => { rec[k] = vals[i]; });
    items.push(rec);
  }
  return { columns: COLS, items };
}

// 別添3 地域指数：各行 名称=最初の文字列、指数=その後の最初の数値
function parseRegionSheet(ws) {
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
  const out = [];
  for (const cells of rows) {
    if (!cells) continue;
    let name = null, val = null;
    for (let i = 0; i < cells.length; i++) {
      const v = cells[i];
      if (typeof v === 'string' && v.trim() !== '') {
        name = v.trim();
        for (let j = i + 1; j < cells.length; j++) { if (isNum(cells[j])) { val = cells[j]; break; } }
        break;
      }
    }
    if (name && val !== null) out.push({ name, val });
  }
  return out;
}
function parseRegional(wb) {
  const prefName = wb.SheetNames.find((n) => n.includes('都道府県')) || wb.SheetNames[0];
  const hwName = wb.SheetNames.find((n) => n.includes('HW') || n.includes('ＨＷ')) || wb.SheetNames[1];
  const prefecture = parseRegionSheet(wb.Sheets[prefName]).map((x) => ({ name: x.name, index: x.val }));
  let hellowork = [];
  if (hwName && wb.Sheets[hwName]) {
    hellowork = parseRegionSheet(wb.Sheets[hwName]).map((x) => {
      const m = x.name.match(/^(\d{4})\s+(.*)$/);
      return { code: m ? m[1] : null, name: m ? m[2] : x.name, index: x.val };
    });
  }
  return { prefecture, hellowork };
}

const readWorkbook = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => { try { resolve(XLSX.read(e.target.result, { type: 'array' })); } catch (err) { reject(err); } };
  reader.onerror = reject;
  reader.readAsArrayBuffer(file);
});

// ====================== 保存済みデータの閲覧ビューア ======================
const WageViewer = ({ year, data, loading }) => {
  const [jobQ, setJobQ] = useState('');
  const [hwQ, setHwQ] = useState('');

  const cols = (data && data.national && Array.isArray(data.national.columns) && data.national.columns.length)
    ? data.national.columns : COLS;
  const items = (data && data.national && Array.isArray(data.national.items)) ? data.national.items : [];
  const prefecture = (data && data.regional && Array.isArray(data.regional.prefecture)) ? data.regional.prefecture : [];
  const hellowork = (data && data.regional && Array.isArray(data.regional.hellowork)) ? data.regional.hellowork : [];

  const jq = jobQ.trim();
  const filteredItems = jq
    ? items.filter((it) => `${it.code || ''} ${it.name || ''}`.includes(jq)) : items;
  const hq = hwQ.trim();
  const filteredHw = hq
    ? hellowork.filter((h) => `${h.code || ''} ${h.name || ''}`.includes(hq)) : hellowork;

  return (
    <div className="bg-white rounded-xl shadow p-6 mb-6">
      <div className="flex items-center mb-4">
        <h3 className="font-bold text-lg">
          取込データの内容：<span className="text-indigo-600">{data && data.yearLabel ? data.yearLabel : year}</span>
        </h3>
      </div>

      {loading && <div className="text-gray-500 py-6">読み込み中…</div>}
      {!loading && !data && <div className="text-rose-500 py-6">この年度のデータを読み込めませんでした。</div>}

      {!loading && data && (
        <div className="space-y-8">
          {/* 全国平均（職種別） */}
          <div>
            <div className="flex items-baseline gap-3 mb-2">
              <h4 className="font-bold text-gray-700">全国平均（職種別）</h4>
              <span className="text-xs text-gray-400">{filteredItems.length} / {items.length} 職種</span>
              <input value={jobQ} onChange={(e) => setJobQ(e.target.value)} placeholder="職種名・コードで絞り込み"
                className="ml-auto border border-gray-200 rounded px-3 py-1.5 text-xs w-56 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            </div>
            {items.length === 0 ? <div className="text-sm text-gray-400">全国平均データが登録されていません。</div> : (
              <div className="overflow-auto max-h-[28rem] border border-gray-100 rounded-lg">
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="bg-gray-50 text-gray-500 sticky top-0">
                    <tr className="text-left">
                      <th className="px-3 py-2">コード</th>
                      <th className="px-3 py-2">職種名</th>
                      {cols.map((c) => <th key={c} className="px-3 py-2 text-right whitespace-nowrap">{COL_LABELS[c] || c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((it, idx) => (
                      <tr key={(it.code || '') + (it.name || '') + idx} className="border-t border-gray-50">
                        <td className="px-3 py-2 text-gray-500">{it.code || '—'}</td>
                        <td className="px-3 py-2 font-medium text-gray-700">{it.name || '—'}</td>
                        {cols.map((c) => <td key={c} className="px-3 py-2 text-right tabular-nums">{fmtYen(it[c])}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* 地域指数 */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-gray-700 mb-2">地域指数（都道府県）<span className="ml-2 text-xs text-gray-400">{prefecture.length} 件</span></h4>
              {prefecture.length === 0 ? <div className="text-sm text-gray-400">都道府県データが登録されていません。</div> : (
                <div className="overflow-auto max-h-96 border border-gray-100 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 sticky top-0">
                      <tr className="text-left"><th className="px-3 py-2">都道府県</th><th className="px-3 py-2 text-right">指数</th></tr>
                    </thead>
                    <tbody>
                      {prefecture.map((p, idx) => (
                        <tr key={(p.name || '') + idx} className="border-t border-gray-50">
                          <td className="px-3 py-2 text-gray-700">{p.name || '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{typeof p.index === 'number' ? p.index : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div>
              <div className="flex items-baseline gap-2 mb-2">
                <h4 className="font-bold text-gray-700">地域指数（ハローワーク別）</h4>
                <span className="text-xs text-gray-400">{filteredHw.length} / {hellowork.length} 件</span>
                <input value={hwQ} onChange={(e) => setHwQ(e.target.value)} placeholder="名称・コードで絞り込み"
                  className="ml-auto border border-gray-200 rounded px-3 py-1.5 text-xs w-44 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              </div>
              {hellowork.length === 0 ? <div className="text-sm text-gray-400">ハローワーク別データが登録されていません。</div> : (
                <div className="overflow-auto max-h-96 border border-gray-100 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 sticky top-0">
                      <tr className="text-left"><th className="px-3 py-2">コード</th><th className="px-3 py-2">名称</th><th className="px-3 py-2 text-right">指数</th></tr>
                    </thead>
                    <tbody>
                      {filteredHw.map((h, idx) => (
                        <tr key={(h.code || '') + (h.name || '') + idx} className="border-t border-gray-50">
                          <td className="px-3 py-2 text-gray-500">{h.code || '—'}</td>
                          <td className="px-3 py-2 text-gray-700">{h.name || '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{typeof h.index === 'number' ? h.index : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const UploadSlot = ({ title, hint, onParsed, summary, error }) => {
  const ref = useRef(null);
  return (
    <div className="border-2 border-dashed border-gray-200 rounded-xl p-5 text-center"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); onParsed(e.dataTransfer.files[0]); }}>
      <div className="font-bold text-gray-700 mb-1">{title}</div>
      <div className="text-xs text-gray-400 mb-3">{hint}</div>
      <button onClick={() => ref.current?.click()} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-indigo-700">Excelを選択</button>
      <input ref={ref} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => onParsed(e.target.files[0])} />
      {summary && <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">{summary}</div>}
      {error && <div className="mt-3 text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{error}</div>}
    </div>
  );
};

const WageDataAdmin = () => {
  const role = localStorage.getItem('admin_role');
  const canEdit = role === 'super_admin' || role === 'system_admin';

  const [years, setYears] = useState([]);
  const [year, setYear] = useState('');
  const [yearLabel, setYearLabel] = useState('');
  const [national, setNational] = useState(null);
  const [regional, setRegional] = useState(null);
  const [natErr, setNatErr] = useState('');
  const [regErr, setRegErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewYear, setViewYear] = useState('');
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const loadYears = useCallback(() => {
    adminFetch(`/api/admin-report.php?action=wage_years&t=${Date.now()}`)
      .then((r) => r.json()).then((d) => setYears(Array.isArray(d) ? d : [])).catch(() => setYears([]));
  }, []);
  useEffect(() => { loadYears(); }, [loadYears]);

  const viewWage = useCallback((y) => {
    setViewYear(y); setViewData(null); setViewLoading(true);
    adminFetch(`/api/admin-report.php?action=wage_data&year=${encodeURIComponent(y)}&t=${Date.now()}`)
      .then((r) => r.json()).then((d) => setViewData(d && typeof d === 'object' ? d : null))
      .catch(() => setViewData(null)).finally(() => setViewLoading(false));
  }, []);

  // 最初から常に表示：年度が読み込まれたら、表示中の年度が無い／消えた場合は最新年度を自動で開く
  useEffect(() => {
    if (years.length === 0) { setViewYear(''); setViewData(null); return; }
    if (!viewYear || !years.some((y) => y.year === viewYear)) viewWage(years[0].year);
  }, [years, viewYear, viewWage]);

  const handleNational = async (file) => {
    if (!file) return; setNatErr(''); setNational(null);
    try {
      const wb = await readWorkbook(file);
      const data = parseNational(wb.Sheets[wb.SheetNames[0]]);
      if (!data.items.length) { setNatErr('職種データが見つかりませんでした。別添2のExcelか確認してください。'); return; }
      setNational(data);
    } catch (e) { setNatErr('解析に失敗しました：' + e.message); }
  };
  const handleRegional = async (file) => {
    if (!file) return; setRegErr(''); setRegional(null);
    try {
      const wb = await readWorkbook(file);
      const data = parseRegional(wb);
      if (!data.prefecture.length) { setRegErr('地域指数が見つかりませんでした。別添3のExcelか確認してください。'); return; }
      setRegional(data);
    } catch (e) { setRegErr('解析に失敗しました：' + e.message); }
  };

  const save = async (kind, payload) => {
    if (!year.trim()) { setMsg('年度を入力してください（例：2026）'); return; }
    setSaving(true); setMsg('');
    try {
      const res = await adminFetch('/api/admin-report.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_wage', year: year.trim(), yearLabel: yearLabel.trim(), kind, payload }),
      });
      const d = await res.json();
      if (d.success) { setMsg(kind === 'national' ? '全国平均を保存しました' : '地域指数を保存しました'); loadYears(); }
      else setMsg(d.message || '保存に失敗しました');
    } catch { setMsg('保存に失敗しました'); }
    setSaving(false);
  };

  const removeYear = async (y) => {
    if (!window.confirm(`${y} の派遣賃金データを削除しますか？`)) return;
    await adminFetch('/api/admin-report.php', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete_wage', year: y }),
    });
    loadYears(); // 表示中の年度を削除した場合は自動表示 effect が最新年度へ切り替える
  };

  if (!canEdit) {
    return <div><h2 className="text-2xl font-bold mb-6">派遣賃金データ</h2>
      <div className="bg-white rounded-xl shadow p-8 text-gray-500">この機能はスーパー／システム管理者のみ利用できます。</div></div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">派遣賃金データ</h2>
      <p className="text-xs text-gray-500 mb-6">
        厚労省の局長通達「一般労働者の賃金水準」の公式Excel（別添2＝全国平均／別添3＝地域指数）をアップロードして年度ごとに保存します。組織人員構成分析レポートの適正賃金で使用します。
      </p>

      {/* 登録済み年度 */}
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <h3 className="font-bold mb-4">登録済み年度</h3>
        {years.length === 0 ? <div className="text-sm text-gray-400">まだ登録がありません。</div> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-2">年度</th><th>名称</th><th className="text-right">全国(職種)</th><th className="text-right">都道府県</th><th className="text-right">HW別</th><th></th>
            </tr></thead>
            <tbody>
              {years.map((y) => (
                <tr key={y.year} className={`border-b border-gray-50 ${viewYear === y.year ? 'bg-indigo-50/50' : ''}`}>
                  <td className="py-2 font-bold text-gray-700">{y.year}</td>
                  <td className="text-gray-500">{y.yearLabel || '—'}</td>
                  <td className="text-right">{y.national || 0}</td>
                  <td className="text-right">{y.prefecture || 0}</td>
                  <td className="text-right">{y.hellowork || 0}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => viewWage(y.year)} disabled={viewYear === y.year}
                      className={`px-3 py-1 rounded text-[10px] font-bold mr-1 ${viewYear === y.year ? 'bg-indigo-600 text-white cursor-default' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
                      {viewYear === y.year ? '表示中' : '表示'}
                    </button>
                    <button onClick={() => removeYear(y.year)} className="bg-red-50 text-red-600 px-3 py-1 rounded text-[10px] font-bold hover:bg-red-100">削除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 取込データの閲覧 */}
      {viewYear && <WageViewer year={viewYear} data={viewData} loading={viewLoading} />}

      {/* 新規登録/更新 */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-bold mb-4">Excelから登録／更新</h3>
        {msg && <div className="mb-4 text-sm text-indigo-700 bg-indigo-50 rounded-lg px-4 py-2 inline-block">{msg}</div>}
        <div className="flex flex-wrap gap-4 mb-6">
          <div>
            <label className="block text-xs text-gray-500 mb-1">年度（半角・例：2026）</label>
            <input value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">名称（任意・例：令和8年度）</label>
            <input value={yearLabel} onChange={(e) => setYearLabel(e.target.value)} placeholder="令和8年度"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <UploadSlot title="① 全国平均（別添2）" hint="職業安定業務統計による一般基本給・賞与等（時給換算）"
              onParsed={handleNational} error={natErr}
              summary={national ? `解析OK：${national.items.length} 職種` : ''} />
            {national && (
              <button onClick={() => save('national', national)} disabled={saving}
                className="mt-3 w-full bg-emerald-600 text-white py-2.5 rounded-lg text-sm font-bold hover:bg-emerald-700 disabled:opacity-50">
                {saving ? '保存中…' : 'この年度に「全国平均」を保存'}
              </button>
            )}
          </div>
          <div>
            <UploadSlot title="② 地域指数（別添3）" hint="都道府県別・ハローワーク別の地域指数"
              onParsed={handleRegional} error={regErr}
              summary={regional ? `解析OK：都道府県 ${regional.prefecture.length} / HW別 ${regional.hellowork.length}` : ''} />
            {regional && (
              <button onClick={() => save('regional', regional)} disabled={saving}
                className="mt-3 w-full bg-emerald-600 text-white py-2.5 rounded-lg text-sm font-bold hover:bg-emerald-700 disabled:opacity-50">
                {saving ? '保存中…' : 'この年度に「地域指数」を保存'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WageDataAdmin;
